import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Button, Dialog, Flex, Link, Text } from '@raystack/apsara';
import {
  FrontierServiceQueries,
  useMutation,
  create
} from '@raystack/frontier/hooks';
import { UpdateCurrentUserRequestSchema } from '@raystack/frontier';

import { IdentityContext } from '@src/contexts';

import styles from './OrderDeskPromotionPopup.module.css';

interface OrderDeskPromotionPopup {
  onOpenChange: React.ComponentProps<typeof Dialog>['onOpenChange'];
  getTriggerElement: () => HTMLDivElement | null;
}

export const OrderDeskPromotionPopup = ({
  onOpenChange,
  getTriggerElement
}: OrderDeskPromotionPopup) => {
  const isPopupToggledOpen = useRef(false);

  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>();

  const { user, refetchUser } = useContext(IdentityContext);
  const { mutateAsync: updateUserMutation } = useMutation(
    FrontierServiceQueries.updateCurrentUser
  );

  const toggleOpen = useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
      setOpen(open);
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (!user?.id || isPopupToggledOpen.current) return;

    const { is_orderdesk_onboarded } = (user?.metadata ?? {
      is_orderdesk_onboarded: false
    }) as { is_orderdesk_onboarded: boolean };

    if (!is_orderdesk_onboarded) {
      isPopupToggledOpen.current = true;
      toggleOpen(true);
    }
  }, [user?.id, user?.metadata, toggleOpen]);

  useEffect(() => {
    const elem = getTriggerElement();
    if (!elem) return;

    function updateStyle(elem: ReturnType<typeof getTriggerElement>) {
      if (!elem) return;

      const { top, right } = elem.getBoundingClientRect();

      setStyle({
        top,
        left: `calc(${right + 20}px)`,
        transform: `translate(0%, 0%)`
      });
    }

    const ob = new ResizeObserver(() => {
      updateStyle(getTriggerElement());
    });
    ob.observe(elem);

    return () => {
      ob.disconnect();
    };
  }, [getTriggerElement]);

  const handleClick = useCallback(async () => {
    if (!user) return;

    await updateUserMutation(
      create(UpdateCurrentUserRequestSchema, {
        body: {
          email: user.email!,
          metadata: { ...(user?.metadata ?? {}), is_orderdesk_onboarded: true }
        }
      })
    );

    // Refetch user in IdentityContext
    refetchUser?.();

    toggleOpen(false);
  }, [toggleOpen, user, updateUserMutation, refetchUser]);

  return (
    <Dialog open={open && !!style} onOpenChange={toggleOpen}>
      <Dialog.Content
        className={styles.dialogContent}
        style={style}
        overlayClassName={styles.overlapStyles}
      >
        <Flex direction='column' gap={7}>
          <Flex direction='column' gap={3}>
            <Text size='large' weight='medium'>
              Order Desk
            </Text>

            <Text size='regular' variant='secondary'>
              Order Desk is where you place and manage satellite image capture
              requests. It gives you full control over tasking parameters,
              status tracking, and delivery timelines—all in one streamlined
              workspace.{' '}
              <Link
                external
                size='regular'
                href='https://docs.pixxel.space/documentation/tasking/navigatingtoOrderdesk'
                data-test-id='read-more-about-order-desk-link'
              >
                Read more
              </Link>
            </Text>
          </Flex>

          <div
            className={styles.image}
            style={{
              backgroundImage: `url(${window.location.origin}/images/orderdesk-ss.png)`
            }}
          />

          <Button
            onClick={handleClick}
            data-test-id='close-orderdesk-promo'
            className={styles.btn}
          >
            Got it
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
